import { useInfiniteQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export interface LcPoolSummary {
  poolId: string;
  name: string;
  description: string | null;
  tokenA: {
    itemDefId: string | null;
    symbol: string;
    name: string;
    decimals: number;
    source: string;
  };
  tokenB: {
    itemDefId: string | null;
    symbol: string;
    name: string;
    decimals: number;
    source: string;
  };
  reserveA: string;
  reserveB: string;
  totalLpShares: string;
  feeBps: number;
  minLiquidity: number;
  maxTradePct: number;
  status: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

interface LcPoolListResult {
  pools: LcPoolSummary[];
  total: number;
  limit: number;
  offset: number;
}

interface UseLcPoolsReturn {
  pools: LcPoolSummary[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  loadMore: () => void;
  hasMore: boolean;
}

const LIMIT = 20;

export function useLiquidityCachePools(): UseLcPoolsReturn {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: queryKeys.liquidityCaches.pools(),
      queryFn: async ({ pageParam = 0 }): Promise<LcPoolListResult> => {
        const result = await api.listLiquidityCachePools({
          limit: LIMIT,
          offset: pageParam,
        });
        return result;
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const next = allPages.length * LIMIT;
        return next < lastPage.total ? next : undefined;
      },
    });

  const pools = data?.pages.flatMap((p) => p.pools) ?? [];
  const total = data?.pages[0]?.total ?? 0;

  return {
    pools,
    total,
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load pools' : null,
    refresh: () => {
      refetch();
    },
    loadMore: () => {
      if (hasNextPage && !isFetchingNextPage) fetchNextPage();
    },
    hasMore: !!hasNextPage,
  };
}
